import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  FEATURE_ALI_LOOKBACK,
  JURISDICTION_VIEW,
} from '@rsos/capstone/src/constants/capabilities';
import { useCurrentPSAPAccountID } from '@rsos/reduxDataStore/src/features/capstone/psapInfo';
import { getALILocationHistory } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsAPI';
import { addLocation } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import { getJitteredSettleMs } from '@rsos/reduxDataStore/src/utils/useStreamWsReconnectTask';
import checkAniAliAccessSelector from '@rsos/utils/checkAniAliAccessSelector';
import isAgencyShareChatEnabled from '@rsos/utils/isAgencyShareChatEnabled';
// addLocToQueueSuccess is the JV / Agency-Share-Chat variant of addLocation,
// matching how live websocket-server locations are dispatched in StreamWS.js.
import { addLocToQueueSuccess } from '../../../actions';
import { useAppDispatch } from '../../../app';
import type { RootState } from '../../../app';

// After a successful reconnect, wait the shared settle delay (plus random
// jitter, so a fleet reconnecting together doesn't look back in lockstep) and
// confirm the socket is still connected, so a flapping connection doesn't fire
// a burst of lookbacks.
// While reconnect attempts keep failing, perform at most one lookback per this
// interval.
const FALLBACK_THROTTLE_MS = 30_000;
// Look back slightly before the disconnect to avoid missing a record that
// landed in the same moment the connection dropped.
const DISCONNECT_LOOKBACK_BUFFER_MS = 30_000;
// Always look back at least this far. The websocket-server can stop delivering
// data before the disconnect is detected (e.g. during a failover), so the
// disconnect timestamp alone can under-count the gap.
const MIN_LOOKBACK_WINDOW_MS = 5 * 60 * 1_000;
// The history endpoint rejects a `calls_after` older than 30 minutes, so the
// window is clamped to this regardless of how long the outage has lasted.
const MAX_LOOKBACK_WINDOW_MS = 30 * 60 * 1_000;

interface AliLocationRecord {
  caller_id?: string;
  [key: string]: unknown;
}

// The shared global typing for `window.__RSOS.streamWS.instance` (declared in
// shared/debug-panel) does not include the close-callback / isConnected surface
// this hook relies on. Type the instance locally rather than widening that
// shared declaration, which would fan out builds/approvals across every app
// that depends on debug-panel.
interface AliLookbackReconnectInfo {
  disconnectedAt?: number;
}

interface AliLookbackCloseInfo {
  reconnectionCount: number;
  disconnectedAt?: number;
}

interface AliLookbackStreamWS {
  addOnMessageCallback: (callbacks: {
    [key: string]: (message: Record<string, unknown>) => void;
  }) => void;
  removeOnMessageCallback: (messageName: string) => void;
  addOnReconnectCallback: (callbacks: {
    [key: string]: (info?: AliLookbackReconnectInfo) => void;
  }) => void;
  removeOnReconnectCallback: (callbackName: string) => void;
  addOnCloseCallback: (callbacks: {
    [key: string]: (info: AliLookbackCloseInfo) => void;
  }) => void;
  removeOnCloseCallback: (callbackName: string) => void;
  isConnected: () => boolean;
}

/**
 * Backfills ALI location records that may have been missed while the
 * websocket-server connection was down. On disconnect the disconnect time is
 * recorded; when the connection comes back (or while reconnect attempts keep
 * failing) the recent ALI history is fetched and the records are dispatched as
 * if they had arrived over the websocket-server connection.
 *
 * Gated behind the `feature_ali_lookback_0` authority capability and ANI/ALI
 * access. Registers on the shared StreamWS instance, mirroring the other
 * streamWS_hooks.
 */
const useHandleAliLookback = (): void => {
  const StreamWS = window?.__RSOS?.streamWS?.instance as
    | AliLookbackStreamWS
    | undefined;
  const dispatch = useAppDispatch();

  const isLookbackEnabled = useSelector(
    (state: RootState) =>
      !!state.psaps?.currentPSAP?.activeCapabilities?.[FEATURE_ALI_LOOKBACK],
  );
  const hasAniAliAccess = useSelector(checkAniAliAccessSelector);
  const accountId = useCurrentPSAPAccountID();
  const orgName = useSelector(
    (state: RootState) => state.sinatra?.user?.currentOrg?.name,
  );
  const isJVEnabled = useSelector(
    (state: RootState) =>
      !!state.psaps?.currentPSAP?.activeCapabilities?.[JURISDICTION_VIEW],
  );
  const isShareChatEnabled = useSelector((state: RootState) =>
    isAgencyShareChatEnabled(state.psaps),
  );

  const enabled = Boolean(isLookbackEnabled && hasAniAliAccess && accountId);

  // Mutable, render-independent state for the lookback lifecycle.
  const disconnectTimeRef = useRef<number | null>(null);
  const lastLookbackStartRef = useRef<number | null>(null);
  const reconnectedAtRef = useRef<number | null>(null);
  const settleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const dispatchLocation = useCallback(
    (record: AliLocationRecord) => {
      if (!record?.caller_id) {
        return;
      }
      // Mirror StreamWS.handleLocations: JV / Agency-Share-Chat use
      // addLocToQueueSuccess, everything else uses addLocation.
      if (isJVEnabled || isShareChatEnabled) {
        dispatch(addLocToQueueSuccess(record));
      } else {
        dispatch(addLocation(record));
      }
    },
    [dispatch, isJVEnabled, isShareChatEnabled],
  );

  // Fires a single lookback request and dispatches the returned records.
  // Records the request start time only on success and resolves to whether the
  // request succeeded so callers can decide whether to retry. Throws nothing.
  const attemptLookback = useCallback(async (): Promise<boolean> => {
    const disconnectTime = disconnectTimeRef.current;
    if (disconnectTime == null || !accountId || !orgName) {
      return false;
    }

    const now = Date.now();
    // Look back at least MIN_LOOKBACK_WINDOW_MS, reach further back to cover a
    // longer outage (disconnect time minus a small buffer), and never exceed
    // the endpoint's 30-minute limit.
    const callsAfter = Math.max(
      now - MAX_LOOKBACK_WINDOW_MS,
      Math.min(
        now - MIN_LOOKBACK_WINDOW_MS,
        disconnectTime - DISCONNECT_LOOKBACK_BUFFER_MS,
      ),
    );

    try {
      const { data } = await getALILocationHistory({
        accountId,
        callsAfter,
        orgName,
      });
      // Record the time the (successful) request was started.
      lastLookbackStartRef.current = now;
      // The endpoint wraps the records in an envelope:
      // { account_id, calls_after, count, locations: [...] }.
      const records: AliLocationRecord[] = Array.isArray(data?.locations)
        ? data.locations
        : [];
      records.forEach(dispatchLocation);
      return true;
    } catch {
      return false;
    }
  }, [accountId, orgName, dispatchLocation]);

  // Reconnect succeeded: wait for the connection to settle, confirm it is still
  // connected, then backfill. Retry once on failure.
  const handleReconnect = useCallback(
    (info?: AliLookbackReconnectInfo) => {
      // The client does not notify close callbacks on the first unintended
      // close, so the reconnect callback is the one place the outage start is
      // always delivered. Keep the earliest start if a close already recorded
      // one.
      // Without it, treat the reconnect as the outage start so at least the
      // minimum window is still looked back.
      const reconnectedAt = Date.now();
      reconnectedAtRef.current = reconnectedAt;
      const disconnectedAt = info?.disconnectedAt ?? reconnectedAt;
      disconnectTimeRef.current =
        disconnectTimeRef.current == null
          ? disconnectedAt
          : Math.min(disconnectTimeRef.current, disconnectedAt);
      if (settleTimerRef.current) {
        clearTimeout(settleTimerRef.current);
      }
      settleTimerRef.current = setTimeout(async () => {
        settleTimerRef.current = null;
        if (!StreamWS?.isConnected()) {
          return;
        }
        // A lookback that started after the socket reopened already covers
        // everything since. One that ran while the socket was still down
        // cannot include later records, so the reconnect lookback still runs,
        // reaching back to that lookback's start if it is earlier than the
        // outage start.
        const lastStart = lastLookbackStartRef.current;
        const reopenedAt = reconnectedAtRef.current;
        if (
          lastStart != null &&
          reopenedAt != null &&
          lastStart >= reopenedAt
        ) {
          disconnectTimeRef.current = null;
          return;
        }
        if (lastStart != null && disconnectTimeRef.current != null) {
          disconnectTimeRef.current = Math.min(
            disconnectTimeRef.current,
            lastStart,
          );
        }
        const succeeded = await attemptLookback();
        if (!succeeded) {
          await attemptLookback();
        }
        disconnectTimeRef.current = null;
      }, getJitteredSettleMs());
    },
    [StreamWS, attemptLookback],
  );

  // Unintended close. The client stays quiet on the first close, so this only
  // runs once a reconnect attempt has failed (or the watchdog found a dead
  // socket); reconnectionCount === 0 is that watchdog case.
  const handleClose = useCallback(
    ({ reconnectionCount, disconnectedAt }: AliLookbackCloseInfo) => {
      if (disconnectTimeRef.current == null) {
        disconnectTimeRef.current = disconnectedAt ?? Date.now();
      }
      if (reconnectionCount === 0) {
        return;
      }
      // Reconnect attempt failed: backfill if the last lookback was over the
      // throttle window ago (or none has run yet).
      const lastStart = lastLookbackStartRef.current;
      if (lastStart == null || Date.now() - lastStart > FALLBACK_THROTTLE_MS) {
        void attemptLookback();
      }
    },
    [attemptLookback],
  );

  useEffect(() => {
    if (!StreamWS || !enabled) {
      return;
    }

    StreamWS.addOnCloseCallback({ handleAliLookbackClose: handleClose });
    StreamWS.addOnReconnectCallback({
      handleAliLookbackReconnect: handleReconnect,
    });

    return () => {
      StreamWS.removeOnCloseCallback('handleAliLookbackClose');
      StreamWS.removeOnReconnectCallback('handleAliLookbackReconnect');
      // A stale outage start must not widen the window of a later, unrelated one.
      disconnectTimeRef.current = null;
      reconnectedAtRef.current = null;
      if (settleTimerRef.current) {
        clearTimeout(settleTimerRef.current);
        settleTimerRef.current = null;
      }
    };
  }, [StreamWS, enabled, handleClose, handleReconnect]);
};

export default useHandleAliLookback;
